import React from 'react';
import { Search, MessageSquare, Bot, Clock, UserCheck, CheckCircle, Inbox } from 'lucide-react';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { cn, formatRelativeTime, getStatusLabel, getStatusColor, truncate, getInitials } from '../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const ConversationList = ({ 
  conversations,
  closedConversations = [],
  selectedId, 
  onSelect, 
  filter, 
  onFilterChange,
  searchTerm,
  onSearchChange 
}) => {
  const isClosedFilter = filter === 'closed';
  const displayConversations = isClosedFilter ? closedConversations : conversations;
  
  const filteredConversations = displayConversations.filter(conv => {
    if (isClosedFilter) return true; // Already filtered from backend
    const matchesFilter = filter === 'all' || conv.status === filter;
    const matchesSearch = !searchTerm || 
      conv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.customer_phone?.includes(searchTerm);
    return matchesFilter && matchesSearch;
  });

  const counts = {
    all: conversations.length,
    bot: conversations.filter(c => c.status === 'bot').length,
    waiting: conversations.filter(c => c.status === 'waiting').length,
    active: conversations.filter(c => c.status === 'active').length,
    closed: closedConversations.length,
  };

  const filterOptions = [
    { value: 'all', label: 'Todas', icon: Inbox, count: counts.all, color: 'text-zinc-400' },
    { value: 'bot', label: 'Bot', icon: Bot, count: counts.bot, color: 'text-blue-400' },
    { value: 'waiting', label: 'Aguardando', icon: Clock, count: counts.waiting, color: 'text-yellow-400' },
    { value: 'active', label: 'Ativos', icon: UserCheck, count: counts.active, color: 'text-green-400' },
    { value: 'closed', label: 'Finalizadas', icon: CheckCircle, count: counts.closed, color: 'text-zinc-500' },
  ];

  return (
    <div className="conversation-list" data-testid="conversation-list">
      <div className="p-4 border-b border-border">
        <h2 className="text-lg font-semibold font-mono mb-3">Conversas</h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input
            data-testid="conversation-search"
            placeholder="Buscar conversa..."
            className="pl-9 bg-secondary border-0"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>
      
      {/* Filter Tabs - Redesigned */}
      <div className="px-3 py-3 border-b border-border">
        <div className="flex gap-1 flex-wrap">
          {filterOptions.map((opt) => {
            const Icon = opt.icon;
            const isActive = filter === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => onFilterChange(opt.value)}
                className={cn(
                  "flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-medium transition-all whitespace-nowrap",
                  isActive 
                    ? "bg-primary text-primary-foreground shadow-sm" 
                    : "bg-secondary/70 text-muted-foreground hover:bg-secondary hover:text-foreground"
                )}
              >
                <Icon size={12} className={isActive ? "" : opt.color} />
                <span>{opt.label}</span>
                {opt.count > 0 && (
                  <span className={cn(
                    "min-w-[16px] h-[16px] rounded-full flex items-center justify-center text-[9px] font-bold",
                    isActive 
                      ? "bg-primary-foreground/20 text-primary-foreground" 
                      : "bg-background/50 text-muted-foreground"
                  )}>
                    {opt.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="py-2">
          {filteredConversations.length === 0 ? (
            <div className="empty-state py-8">
              <MessageSquare className="w-12 h-12 mb-2 opacity-30" />
              <p className="text-sm">
                {isClosedFilter ? 'Nenhuma conversa finalizada' : 'Nenhuma conversa encontrada'}
              </p>
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <div
                key={conv.id}
                data-testid={`conversation-item-${conv.id}`}
                onClick={() => onSelect(conv)}
                className={cn(
                  'conversation-item',
                  selectedId === conv.id && 'selected'
                )}
              >
                <div className="flex items-start gap-3">
                  <Avatar className="w-10 h-10 bg-secondary">
                    {conv.customer_avatar_url && (
                      <AvatarImage
                        src={conv.customer_avatar_url.startsWith('http') ? conv.customer_avatar_url : `${BACKEND_URL}${conv.customer_avatar_url}`}
                        alt={conv.customer_name || conv.customer_phone}
                      />
                    )}
                    <AvatarFallback className="bg-secondary text-muted-foreground text-sm">
                      {getInitials(conv.customer_name)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm truncate">
                        {conv.customer_name || conv.customer_phone}
                      </span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatRelativeTime(conv.last_message_time)}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <p className="text-xs text-muted-foreground line-clamp-2 break-words">
                        {conv.last_message || 'Sem mensagens'}
                      </p>
                      {conv.unread_count > 0 && (
                        <span className="unread-badge flex-shrink-0">{conv.unread_count}</span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className={cn('text-xs px-1.5 py-0', getStatusColor(conv.status))}>
                        {getStatusLabel(conv.status)}
                      </Badge>
                      {conv.assigned_name && (
                        <span className="text-xs text-muted-foreground">
                          → {conv.assigned_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default ConversationList;
